/**
 * StatusPagesPage.tsx — /app/status-pages — status page builder
 *
 * Purpose:    List + create/edit status pages with components and publish toggle.
 * Inputs:     statusPagesApi + billingApi from @/lib/saas-client
 * Outputs:    Status page builder UI; links to public /s/:slug
 * Constraints: Dark-first styling; WCAG AA; free tier limited to 1 page
 * SOT:        nsentry SaaS app zone
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { statusPagesApi, billingApi, type StatusPage, type StatusPageComponent } from '@/lib/saas-client'
import { TIERS, type TierId } from '@/lib/tiers'
import type { SentryError } from '@/lib/sentry-error'

const COMPONENT_STATUS_STYLES: Record<StatusPageComponent['status'], string> = {
  operational: 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300',
  degraded: 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
  down: 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300',
}

const SLUG_PATTERN = /^[a-z0-9-]+$/

export function StatusPagesPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [pages, setPages] = useState<StatusPage[]>([])
  const [tier, setTier] = useState<TierId>('free')
  const [error, setError] = useState<SentryError | undefined>()
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  async function load() {
    setState('loading')
    const [pagesRes, billingRes] = await Promise.all([statusPagesApi.list(), billingApi.get()])
    if (!pagesRes.ok) {
      setError(pagesRes.error)
      setState('error')
      return
    }
    setPages(pagesRes.value)
    if (billingRes.ok) setTier(billingRes.value.tier)
    setState(pagesRes.value.length === 0 ? 'empty' : 'success')
  }

  useEffect(() => {
    void load()
  }, [])

  const limit = TIERS[tier].statusPages
  const atLimit = pages.length >= limit

  async function handleCreate(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!SLUG_PATTERN.test(slug)) {
      setFormError('Slug must be lowercase letters, numbers, and hyphens only')
      return
    }
    const result = await statusPagesApi.create({ slug, title })
    if (!result.ok) {
      setFormError(result.error.message)
      return
    }
    setShowForm(false)
    setTitle('')
    setSlug('')
    await load()
  }

  async function togglePublish(page: StatusPage) {
    await statusPagesApi.update(page.id, { published: !page.published })
    await load()
  }

  async function addComponent(page: StatusPage) {
    const name = prompt('Component name?')
    if (!name) return
    const components: StatusPageComponent[] = [
      ...page.components,
      { id: `c-${Date.now()}`, name, status: 'operational' },
    ]
    await statusPagesApi.update(page.id, { components })
    await load()
  }

  async function removeComponent(page: StatusPage, componentId: string) {
    const components = page.components.filter((c) => c.id !== componentId)
    await statusPagesApi.update(page.id, { components })
    await load()
  }

  async function setComponentStatus(page: StatusPage, componentId: string, status: StatusPageComponent['status']) {
    const components = page.components.map((c) => (c.id === componentId ? { ...c, status } : c))
    await statusPagesApi.update(page.id, { components })
    await load()
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this status page?')) return
    await statusPagesApi.remove(id)
    await load()
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Status Pages</h1>
          <p className="mt-1 text-sm text-brand-muted">
            {pages.length} / {limit} status page{limit === 1 ? '' : 's'} ({TIERS[tier].label})
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          disabled={atLimit}
          className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          New status page
        </button>
      </div>

      {atLimit && (
        <p className="mb-4 rounded-lg border border-sky-200 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 px-4 py-3 text-sm text-sky-700 dark:text-sky-300">
          Free tier limit reached (1 status page). <Link to="/app/billing" className="font-medium underline">Upgrade</Link> for more.
        </p>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 space-y-4 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Create status page">
          {formError && <p className="text-sm text-red-700 dark:text-red-300" role="alert">{formError}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="sp-title" className="mb-1 block text-sm font-medium text-brand-secondary">Title</label>
              <input
                id="sp-title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="sp-slug" className="mb-1 block text-sm font-medium text-brand-secondary">Slug</label>
              <input
                id="sp-slug"
                required
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="my-status"
                pattern="[a-z0-9-]+"
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400">
              Create
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-md border border-brand-card px-4 py-2 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      <AsyncScreen
        state={state}
        error={error}
        onRetry={load}
        emptyMessage="No status pages yet"
        emptyAction={
          <button type="button" onClick={() => setShowForm(true)} className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500">
            Create your first status page
          </button>
        }
      >
        <div className="space-y-4">
          {pages.map((page) => (
            <div key={page.id} className="rounded-xl border border-brand-subtle bg-brand-surface p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-medium text-brand-ink">{page.title}</h3>
                  <a href={`/s/${page.slug}`} target="_blank" rel="noreferrer" className="text-xs text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
                    View public page →
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-brand-muted">
                    <input
                      type="checkbox"
                      checked={page.published}
                      onChange={() => togglePublish(page)}
                      className="h-4 w-4 rounded border-gray-600 text-sky-500 focus-visible:ring-2 focus-visible:ring-sky-500"
                    />
                    Published
                  </label>
                  <button type="button" onClick={() => handleDelete(page.id)} className="text-xs text-red-700 dark:text-red-400 hover:text-red-700 dark:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400">
                    Delete
                  </button>
                </div>
              </div>
              <ul className="mb-3 space-y-2">
                {page.components.map((c) => (
                  <li key={c.id} className="flex items-center justify-between rounded-md bg-brand-bg px-3 py-2 text-sm">
                    <span className="text-gray-200">{c.name}</span>
                    <div className="flex items-center gap-2">
                      <select
                        aria-label={`Status for ${c.name}`}
                        value={c.status}
                        onChange={(e) => setComponentStatus(page, c.id, e.target.value as StatusPageComponent['status'])}
                        className={`rounded-full border-0 px-2 py-0.5 text-xs font-medium ${COMPONENT_STATUS_STYLES[c.status]}`}
                      >
                        <option value="operational">operational</option>
                        <option value="degraded">degraded</option>
                        <option value="down">down</option>
                      </select>
                      <button type="button" onClick={() => removeComponent(page, c.id)} className="text-xs text-red-700 dark:text-red-400 hover:text-red-700 dark:text-red-300">
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => addComponent(page)} className="text-xs font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
                + Add component
              </button>
            </div>
          ))}
        </div>
      </AsyncScreen>
    </div>
  )
}
